/**
 * Client-side work timer / office minutes from local attendance events.
 * Mirrors backend AttendanceService.computeWorkSession for desktop offline + Isar.
 */

export const WORK_WATCH_MAX_HOURS = 16;
export const WORK_WATCH_MAX_SECONDS = WORK_WATCH_MAX_HOURS * 60 * 60;

const TYPE_CHECK_IN = 'CHECK_IN';
const TYPE_CHECK_OUT = 'CHECK_OUT';
const TYPE_BREAK_START = 'BREAK_START';
const TYPE_BREAK_END = 'BREAK_END';

const parseEventAt = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  const raw = String(value).trim().replace(' ', 'T');
  if (!raw) return null;
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? null : ms;
};

const eventMeta = (event) => {
  if (!event || typeof event !== 'object') return {};
  if (event.metadata && typeof event.metadata === 'object') return event.metadata;
  if (event.meta && typeof event.meta === 'object') return event.meta;
  return {};
};

export const isConfirmedCheckIn = (event) => {
  if (!event || String(event.type || '') !== TYPE_CHECK_IN) return false;
  return eventMeta(event).pendingConfirm !== true;
};

export const isActiveCheckOut = (event) => {
  if (!event || String(event.type || '') !== TYPE_CHECK_OUT) return false;
  return eventMeta(event).revoked !== true;
};

const addCappedWorkSeconds = (accumulated, fromMs, toMs, maxSeconds) => {
  if (fromMs == null || toMs == null) return accumulated;
  const add = Math.max(0, Math.floor((toMs - fromMs) / 1000));
  return Math.min(maxSeconds, accumulated + add);
};

/**
 * @param {Array} events - attendance events for one date (any order)
 * @param {number|Date} [nowInput] - evaluation instant
 * @returns {{ accumulatedSeconds: number, running: boolean, runningSinceMs: number|null, elapsedSeconds: number, onBreak: boolean, loggedOut: boolean, checkedIn: boolean }}
 */
export const computeWorkSessionFromEvents = (events, nowInput = Date.now()) => {
  const nowMs = typeof nowInput === 'number' ? nowInput : (nowInput?.getTime?.() ?? Date.now());
  const maxSeconds = WORK_WATCH_MAX_SECONDS;
  const sorted = (Array.isArray(events) ? events : [])
    .slice()
    .sort((a, b) => {
      const am = parseEventAt(a?.eventAt) ?? 0;
      const bm = parseEventAt(b?.eventAt) ?? 0;
      return am - bm;
    });

  let accumulated = 0;
  let workOpen = null;
  let onBreak = false;
  let loggedOut = false;
  let checkedIn = false;

  for (const event of sorted) {
    if (!event?.type) continue;
    const at = parseEventAt(event.eventAt);
    if (at == null) continue;
    const type = String(event.type);

    if (type === TYPE_CHECK_IN) {
      if (!isConfirmedCheckIn(event)) continue;
      checkedIn = true;
      loggedOut = false;
      if (accumulated >= maxSeconds) continue;
      if (workOpen == null && !onBreak) {
        workOpen = at;
      }
    } else if (type === TYPE_BREAK_START) {
      if (workOpen != null) {
        accumulated = addCappedWorkSeconds(accumulated, workOpen, at, maxSeconds);
        workOpen = null;
      }
      onBreak = true;
    } else if (type === TYPE_BREAK_END) {
      onBreak = false;
      loggedOut = false;
      if (accumulated >= maxSeconds) {
        workOpen = null;
        continue;
      }
      if (workOpen == null) {
        workOpen = at;
      }
    } else if (type === TYPE_CHECK_OUT) {
      if (!isActiveCheckOut(event)) continue;
      if (workOpen != null) {
        accumulated = addCappedWorkSeconds(accumulated, workOpen, at, maxSeconds);
        workOpen = null;
      }
      loggedOut = true;
      onBreak = false;
    }
  }

  let running = workOpen != null && !loggedOut && !onBreak && accumulated < maxSeconds;
  let elapsed = accumulated;
  if (running) {
    elapsed = addCappedWorkSeconds(accumulated, workOpen, nowMs, maxSeconds);
    if (elapsed >= maxSeconds) {
      running = false;
      accumulated = maxSeconds;
    }
  }
  elapsed = Math.min(maxSeconds, Math.max(0, elapsed));

  return {
    accumulatedSeconds: Math.min(maxSeconds, accumulated),
    running,
    runningSinceMs: running ? workOpen : null,
    elapsedSeconds: elapsed,
    onBreak,
    loggedOut,
    checkedIn: checkedIn || Boolean(sorted.some((e) => isConfirmedCheckIn(e))),
  };
};

export const officeMinutesFromElapsedSeconds = (elapsedSeconds) => (
  Math.max(0, Math.floor((Number(elapsedSeconds) || 0) / 60))
);

/**
 * Build a break/status DTO shape compatible with GET /attendance/break/status.
 */
export const buildBreakStatusFromEvents = (events, {
  dateStr,
  calendarDate,
  nowMs = Date.now(),
} = {}) => {
  const dayEvents = (Array.isArray(events) ? events : []).filter((e) => {
    const d = String(e?.date || '').slice(0, 10);
    return !dateStr || d === dateStr || String(e?.eventAt || '').startsWith(dateStr);
  });
  const session = computeWorkSessionFromEvents(dayEvents, nowMs);
  const openBreak = [...dayEvents].reverse().find((e) => {
    if (String(e?.type) !== TYPE_BREAK_START) return false;
    const at = parseEventAt(e.eventAt);
    if (at == null) return false;
    // Open if no later BREAK_END / CHECK_OUT after it
    return !dayEvents.some((other) => {
      const otherAt = parseEventAt(other?.eventAt);
      if (otherAt == null || otherAt <= at) return false;
      const t = String(other.type || '');
      return t === TYPE_BREAK_END || (t === TYPE_CHECK_OUT && isActiveCheckOut(other));
    });
  });

  let breakElapsed = 0;
  if (openBreak && session.onBreak) {
    const started = parseEventAt(openBreak.eventAt);
    if (started != null) {
      breakElapsed = Math.max(0, Math.floor((nowMs - started) / 1000));
    }
  }

  return {
    calendarDate: calendarDate || dateStr || null,
    sessionDate: dateStr || null,
    checkedIn: session.checkedIn,
    loggedOut: session.loggedOut,
    onBreak: session.onBreak,
    breakEventId: openBreak?.id || openBreak?._id || null,
    reason: openBreak?.note || eventMeta(openBreak).reason || null,
    breakStartedAt: openBreak?.eventAt || null,
    elapsedSeconds: breakElapsed,
    workRunning: session.running,
    workRunningSince: session.runningSinceMs != null
      ? new Date(session.runningSinceMs).toISOString()
      : null,
    workAccumulatedSeconds: session.accumulatedSeconds,
    workElapsedSeconds: session.elapsedSeconds,
    fromLocal: true,
  };
};

export const filterEventsForDate = (events, dateStr) => {
  const day = String(dateStr || '').slice(0, 10);
  if (!day) return [];
  return (Array.isArray(events) ? events : []).filter((e) => {
    const d = String(e?.date || '').slice(0, 10);
    if (d === day) return true;
    return String(e?.eventAt || '').startsWith(day);
  });
};

export const resolveSessionDateFromEvents = (events, calendarTodayStr) => {
  const today = String(calendarTodayStr || new Date().toLocaleDateString('en-CA')).slice(0, 10);
  const todayEvents = filterEventsForDate(events, today);
  if (todayEvents.some((e) => isConfirmedCheckIn(e))) {
    return today;
  }
  // Prefer most recent date that has a confirmed check-in and no active checkout
  const byDate = new Map();
  for (const event of events || []) {
    const d = String(event?.date || event?.eventAt || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) continue;
    if (!byDate.has(d)) byDate.set(d, []);
    byDate.get(d).push(event);
  }
  const dates = [...byDate.keys()].sort().reverse();
  for (const d of dates) {
    const session = computeWorkSessionFromEvents(byDate.get(d));
    if (session.checkedIn && !session.loggedOut) {
      return d;
    }
  }
  return today;
};
